/** Drag-and-drop / click file selection (FR-01). */

import { useCallback, useId, useRef, useState } from 'react';
import { CloseIcon, FileIcon, UploadIcon, formatBytes } from './primitives';

interface DropzoneProps {
  files: File[];
  onChange: (files: File[]) => void;
  accept: string[];
  multiple?: boolean;
  maxBytes?: number;
  label?: string;
  hint?: string;
  disabled?: boolean;
}

export function Dropzone({
  files,
  onChange,
  accept,
  multiple = false,
  maxBytes,
  label = 'Drop an IFC file here',
  hint,
  disabled = false,
}: DropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const inputId = useId();

  const acceptSet = accept.map((extension) => extension.toLowerCase());

  const accepted = useCallback(
    (candidates: File[]): File[] => {
      const kept: File[] = [];
      for (const file of candidates) {
        const name = file.name.toLowerCase();
        if (!acceptSet.some((extension) => name.endsWith(extension))) {
          setProblem(`${file.name} is not one of ${accept.join(', ')}.`);
          continue;
        }
        if (maxBytes && file.size > maxBytes) {
          setProblem(
            `${file.name} is ${formatBytes(file.size)}, above the ${formatBytes(maxBytes)} limit.`,
          );
          continue;
        }
        kept.push(file);
      }
      return kept;
    },
    [accept, acceptSet, maxBytes],
  );

  const handle = useCallback(
    (candidates: File[]) => {
      setProblem(null);
      const kept = accepted(candidates);
      if (!kept.length) return;
      onChange(multiple ? [...files, ...kept] : [kept[0] as File]);
    },
    [accepted, files, multiple, onChange],
  );

  const remove = (index: number) => onChange(files.filter((_, i) => i !== index));

  return (
    <div>
      <div
        className={`dropzone${dragging ? ' dragging' : ''}${files.length ? ' has-file' : ''}`}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          if (!disabled) handle(Array.from(event.dataTransfer.files));
        }}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(event) => {
          if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            inputRef.current?.click();
          }
        }}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-describedby={inputId}
        aria-disabled={disabled}
      >
        {files.length === 0 ? (
          <>
            <UploadIcon className="dropzone-icon" />
            <strong>{label}</strong>
            <span className="hint">
              or click to choose — {accept.join(', ')}
              {maxBytes ? ` · up to ${formatBytes(maxBytes)}` : ''}
            </span>
          </>
        ) : (
          <div className="stack" style={{ gap: '0.5rem' }}>
            {files.map((file, index) => (
              <div className="file-chip" key={`${file.name}-${file.size}-${index}`}>
                <FileIcon />
                <div className="check-grow">
                  <div className="file-chip-name">{file.name}</div>
                  <div className="hint">{formatBytes(file.size)}</div>
                </div>
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={(event) => {
                    event.stopPropagation();
                    remove(index);
                  }}
                  aria-label={`Remove ${file.name}`}
                >
                  <CloseIcon />
                </button>
              </div>
            ))}
            {multiple && (
              <span className="hint">Click to add more files, or drag them in.</span>
            )}
          </div>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        className="sr-only"
        accept={accept.join(',')}
        multiple={multiple}
        disabled={disabled}
        onChange={(event) => {
          handle(Array.from(event.target.files ?? []));
          // Cleared so re-selecting the same file still fires a change event.
          event.target.value = '';
        }}
      />

      {hint && (
        <p className="hint" id={inputId} style={{ marginTop: '0.5rem' }}>
          {hint}
        </p>
      )}
      {problem && (
        <p className="callout callout-error" style={{ marginTop: '0.5rem' }} role="alert">
          {problem}
        </p>
      )}
    </div>
  );
}
